import {
    Button,
    ButtonGroup,
    Checkbox,
    Form,
    InlineError,
    Labelled,
    Stack,
    TextField,
} from "@shopify/polaris";
import React, {
    useCallback,
    useEffect,
    useReducer,
    useRef,
    useState,
} from "react";
import { useAppContext } from "../../context";
import { arrInvalid, checkRole, getParamByRole, reducerFn } from "../../helper";
import history from "../../history";
import AmazonIdeaTemplateAutocomplete from "../amazon-idea-template/amazon-idea-template-autocomplete";
import { MediaSelectorButtonPolaris } from "../file/MediaSelectorButtonPolaris";
import { ComponentLabelPolaris } from "../shared/ComponentLabelPolaris";
import StoreSelect from "../store/components/StoreSelect";
import AmazonIdeaField from "./amazon-idea-field";
import AmazonIdeaGenerateTemplate from "./amazon-idea-generate-template";
import AmazonIdeaType from "./amazon-idea-type";
import { getStoreIds, sortFields } from "./helper";


export const DEFAULT_AMAZON_IDEA_KPI = 0.5

function AmazonIdeaForm({ onSubmit, loading, value }) {
    const { currentUser } = useAppContext();
    const { isSeller, isChannelLeader } = checkRole(currentUser);
    const isLeader = isSeller || isChannelLeader;
    const param = getParamByRole(currentUser);

    const [state, setState] = useReducer(reducerFn, {
        title: "",
        type: "mug",
        bulletPoint: "",
        kpi: DEFAULT_AMAZON_IDEA_KPI + "",
        description: "",
        keyword: "",
        keyword2: "",
        keyword3: "",
        keyword4: "",
        keyword5: "",
        mockupIDs: [],
        mockups: [],
        isPersonalized: false,
        fields: [],
        storeTypeIDs: [],
        storeTypes: [],
        storeIDs: [],
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (!value) return;

        const {
            title,
            type,
            kpi,
            description,
            keyword,
            keyword2,
            keyword3,
            keyword4,
            keyword5,
            mockups,
            isPersonalized,
            fields,
            storeTypeIDs,
            storeTypes,
            bulletPoint,
        } = value || {};

        const storeIds = getStoreIds(value?.stores);

        const newState = {
            title,
            type,
            description: description || "",
            keyword: keyword || "",
            keyword2: keyword2 || "",
            keyword3: keyword3 || "",
            keyword4: keyword4 || "",
            keyword5: keyword5 || "",
            isPersonalized,
            storeTypeIDs,
            storeTypes,
            bulletPoint,
        };

        if (kpi) {
            newState.kpi = kpi + "";
        }

        if (isPersonalized) {
            newState.fields = arrInvalid(fields) ? [] : sortFields(fields);
        }

        if (!arrInvalid(mockups)) {
            newState.mockups = mockups;
        }

        if (!arrInvalid(storeIds)) {
            newState.storeIDs = storeIds;
        }

        setState(newState);
    }, [value]);

    const handleStateChange = useCallback(
        (value, id) => {

            let kpi = state.kpi;
            if (id === "isPersonalized" && !value) {
                kpi = DEFAULT_AMAZON_IDEA_KPI + ""
            }

            setState({ [id]: value, kpi });

            const error = validateState(value, id);
            setErrors((p) => ({ ...p, ...error }));
        },
        [state.fields, state.isPersonalized],
    );

    const handleValidateStates = async () => {
        let errors = {};
        Object.entries(state).forEach(([key, value]) => {
            const err = validateState(value, key);
            if (err) {
                errors = { ...errors, ...err };
            }
        });

        if (Object.values(errors).filter(Boolean).length > 0) {
            setErrors((p) => ({ ...p, ...errors }));
            return true;
        }

        return false;
    };

    const storeRef = useRef(null);
    const submitRef = useRef(null);
    const handleSubmit = useCallback(async () => {
        submitRef.current = true;
        if (storeRef.current != null) {
            const storeTypeIDs = storeRef.current.getSelected();
            state.storeTypeIDs = storeTypeIDs;
        }

        const hasError = await handleValidateStates();
        if (hasError) return;

        let input = prepareInput(state);
        // if (!isLeader) {
        //     input.kpi = undefined;
        // }
        onSubmit(input);
    }, [onSubmit, state, handleValidateStates, isLeader]);

    return (
        <Form onSubmit={handleSubmit}>
            <Stack vertical>
                <Stack distribution="fill">
                    <div>
                        <ComponentLabelPolaris required label="Title" />
                        <TextField
                            value={state.title}
                            id="title"
                            onChange={handleStateChange}
                            placeholder="Enter title"
                            error={errors?.title || ""}
                        />
                    </div>
                    {/* <Select
                        label="Type"
                        id="type"
                        options={TYPE_OPTIONS}
                        value={state.type}
                        onChange={handleStateChange}
                    /> */}

                    <AmazonIdeaType
                        value={[]}
                        onChange={(value) => {
                            handleStateChange((value || [])[0] || "", "type");
                        }}
                    />
                </Stack>
                {isLeader ? (
                    <Stack distribution="fill">
                        <TextField
                            label="KPI"
                            type="number"
                            id="kpi"
                            step="0.1"
                            value={state.kpi}
                            onChange={handleStateChange}
                            error={errors?.kpi || ""}
                        />
                    </Stack>
                ) : null}
                <Stack distribution="fill">
                    <Stack vertical>
                        <TextField
                            label="Keyword"
                            id="keyword"
                            placeholder={"Enter keyword"}
                            value={state.keyword}
                            onChange={handleStateChange}
                            maxLength={500}
                            helpText={"Maximum 500 characters."}
                        />
                        <TextField
                            id="keyword2"
                            placeholder={"Enter keyword"}
                            value={state.keyword2}
                            onChange={handleStateChange}
                            maxLength={500}
                            helpText={"Maximum 500 characters."}
                        />
                        <TextField
                            id="keyword3"
                            placeholder={"Enter keyword"}
                            value={state.keyword3}
                            onChange={handleStateChange}
                            maxLength={500}
                            helpText={"Maximum 500 characters."}
                        />
                        <TextField
                            id="keyword4"
                            placeholder={"Enter keyword"}
                            value={state.keyword4}
                            onChange={handleStateChange}
                            maxLength={500}
                            helpText={"Maximum 500 characters."}
                        />
                        <TextField
                            id="keyword5"
                            placeholder={"Enter keyword"}
                            value={state.keyword5}
                            onChange={handleStateChange}
                            maxLength={500}
                            helpText={"Maximum 500 characters."}
                        />
                    </Stack>
                </Stack>
                <Stack distribution="fill">
                    <TextField
                        label="Bullet Point"
                        id="bulletPoint"
                        placeholder={"Enter Bullet Point"}
                        multiline={4}
                        value={state.bulletPoint}
                        onChange={handleStateChange}
                    />
                </Stack>
                <Stack distribution="fill">
                    <TextField
                        label="Item Highlight"
                        id="description"
                        placeholder="Enter Item Highlight"
                        multiline={4}
                        value={state.description}
                        onChange={handleStateChange}
                    />
                </Stack>
                <div>
                    <ComponentLabelPolaris label="Mockups" />
                    <MediaSelectorButtonPolaris
                        accept={"image/*"}
                        singleUpload={false}
                        multiple
                        value={state.mockups ? state.mockups : []}
                        onChange={(files) =>
                            handleStateChange(files, "mockups")
                        }
                        sizeSmall
                    />
                    {errors?.mockups && submitRef.current ? (
                        <span style={{ marginTop: 5, display: "block" }}>
                            <InlineError message={errors.mockups} />
                        </span>
                    ) : null}
                </div>

                {/* <StoreTypeAutocomplete
                    ref={storeRef}
                    values={state.storeTypes}
                /> */}

                <div>
                    <Labelled label="Stores" />
                    <StoreSelect
                        value={state.storeIDs}
                        labelHidden
                        filter={{ platform: "amazon", limit: 200 }}
                        onChange={(storeIds) =>
                            handleStateChange(storeIds, "storeIDs")
                        }
                        getValueImmediate
                        allowMultiple
                    />
                </div>

                <Checkbox
                    id="isPersonalized"
                    checked={state.isPersonalized}
                    label="Personalized"
                    onChange={handleStateChange}
                />
                {state.isPersonalized ? (
                    <Stack vertical spacing="extraTight">
                        <AmazonIdeaTemplateAutocomplete
                            setState={setState}
                            param={param}
                        />

                        <AmazonIdeaField
                            fields={state.fields}
                            setState={setState}
                        />
                    </Stack>
                ) : null}

                <Stack distribution="trailing">
                    <ButtonGroup>
                        <Button
                            children="Cancel"
                            onClick={() => {
                                history.goBack();
                            }}
                        />

                        {state?.fields?.length > 0 ? (
                            <AmazonIdeaGenerateTemplate fields={state.fields} />
                        ) : null}

                        <Button
                            submit
                            children="Submit"
                            primary
                            loading={loading}
                        />
                    </ButtonGroup>
                </Stack>
            </Stack>
        </Form>
    );
}

const FIELDS_REQUIRED = {
    title: "Idea title is required",
    // kpi: "Idea kpi is required",
    mockups: "Idea must have at least one mockup",
};

function validateState(value, id) {
    const res = {};

    if (FIELDS_REQUIRED[id]) {
        res[id] = null;

        if (!value) {
            res[id] = FIELDS_REQUIRED[id];
        } else if (typeof value !== "string" && arrInvalid(value)) {
            res[id] = FIELDS_REQUIRED[id];
        }
    }

    return res;
}

// function sortFields(fields) {
//     if (arrInvalid(fields)) return fields;

//     const res = [];
//     for (let field of fields) {
//         if (!field || field.ordering == null) {
//             continue;
//         }

//         res.push(field);
//     }

//     return res;
// }

function prepareInput(state) {
    if (state.kpi) {
        state.kpi = Number(state.kpi);
    }

    if (!state.isPersonalized) {
        state.fields = undefined;
    }

    if (!arrInvalid(state.fields)) {
        state.fields = state.fields.map((field, index) => ({
            type: field.type,
            label: field.label,
            instructions: field.instructions,
            sampleText: field.sampleText,
            min: field.min ? Number(field.min) : undefined,
            max: field.max ? Number(field.max) : undefined,
            placeholder: field.placeholder,
            ordering: index,
            optionsList: (field.optionsList || []).map((opt) => ({
                name: opt.name,
                priceDifference: Number(opt?.priceDifference || 0),
            })),
        }));
    }

    if (!arrInvalid(state.mockups)) {
        state.mockupIDs = Array.from(
            new Set((state.mockups || []).map((i) => i.id)),
        ).filter(Boolean);
    }

    const { mockups, storeTypes, ...input } = state;
    return input;
}

export default AmazonIdeaForm;
